import { MessageContext } from './types';
import { PatternMatchers } from './pattern-matchers';
import { GreetingGenerator } from './greeting-generator';
import { ContextDeterminer } from './context-determiner';
import { GigRoleDetector } from './gig-role-detector';

export type { MessageContext } from './types';

export class ContextAnalyzer {
  static analyzeMessageContext(
    merchantName: string,
    issue: string,
    platformInfo: any,
    orderNumber?: string
  ): MessageContext {
    const searchText = `${merchantName} ${issue} ${orderNumber || ''}`.toLowerCase();
    
    // Handle gig platform role detection
    let gigRole;
    if (platformInfo.transactionType === 'gig_platform') {
      const roleAnalysis = GigRoleDetector.detectRole(issue, merchantName);
      gigRole = {
        role: roleAnalysis.role,
        consumerProtectionApplies: roleAnalysis.consumerProtectionApplies,
        disclaimer: roleAnalysis.disclaimer
      };
    }
    
    // Step 1: For C2C platforms, ALWAYS default to seller unless EXPLICITLY requesting platform support
    if (ContextDeterminer.isC2CPlatform(platformInfo.platform)) {
      // Only contact platform support if EXPLICITLY mentioned
      if (PatternMatchers.matchesPlatformDisputeIndicators(searchText)) {
        return {
          targetRecipient: 'platform',
          communicationStyle: 'formal',
          relationship: 'platform_dispute',
          greeting: `Dear ${GreetingGenerator.getPlatformName(platformInfo.platform)} Support`,
          reasoning: 'User explicitly wants to dispute with or contact the platform support team',
          gigRole
        };
      }
      
      // For ALL other cases on C2C platforms, address the seller
      return {
        targetRecipient: 'seller',
        communicationStyle: 'direct',
        relationship: 'peer_to_peer',
        greeting: GreetingGenerator.getSellerGreeting(merchantName, platformInfo),
        reasoning: `${GreetingGenerator.getPlatformName(platformInfo.platform)} is a C2C platform - addressing the individual seller directly`,
        gigRole
      };
    }

    // Step 2: Check for explicit platform dispute indicators (for non-C2C platforms)
    if (PatternMatchers.matchesPlatformDisputeIndicators(searchText)) {
      return {
        targetRecipient: 'platform',
        communicationStyle: 'formal',
        relationship: 'platform_dispute',
        greeting: `Dear ${GreetingGenerator.getPlatformName(platformInfo.platform)} Support`,
        reasoning: 'User explicitly wants to dispute with or contact the platform support team',
        gigRole
      };
    }

    // Step 3: Check for direct seller contact intent
    if (PatternMatchers.matchesSellerDirectIndicators(searchText)) {
      return {
        targetRecipient: 'seller',
        communicationStyle: 'direct',
        relationship: 'peer_to_peer',
        greeting: GreetingGenerator.getSellerGreeting(merchantName, platformInfo),
        reasoning: 'User explicitly wants to contact the seller directly',
        gigRole
      };
    }

    // Step 4: Check for platform-mediated communication
    if (PatternMatchers.matchesPlatformMediatedIndicators(searchText)) {
      return {
        targetRecipient: 'seller',
        communicationStyle: 'platform_mediated',
        relationship: 'peer_to_peer',
        greeting: GreetingGenerator.getSellerGreeting(merchantName, platformInfo),
        reasoning: 'User wants to communicate through platform messaging system',
        gigRole
      };
    }

    // Step 5: Default logic based on platform type and seller characteristics
    const defaultContext = ContextDeterminer.getDefaultContext(merchantName, platformInfo, issue);
    return {
      ...defaultContext,
      gigRole
    };
  }
}
